import { captureApiError } from '@/utils/sentry'

const BASE_URL = window.location.href.includes('localhost') ? 'https://localhost:7212/api/' : '/api/'
const RESPONSE_BODY_SNIPPET_LENGTH = 500

function truncateBody (text: string): string {
  return text.length > RESPONSE_BODY_SNIPPET_LENGTH
    ? `${text.slice(0, RESPONSE_BODY_SNIPPET_LENGTH)}…`
    : text
}

function getRequestMethod (options: RequestInit): string {
  return (options.method || 'GET').toUpperCase()
}

async function apiRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
  const response = await fetch(`${BASE_URL}${endpoint}`, options)
  const method = getRequestMethod(options)
  const contentType = response.headers.get('content-type')

  // 204 / empty body = success with no payload
  if (response.status === 204) {
    return null
  }

  const text = await response.text()
  if (!text) {
    if (response.ok) return null
    const error = new Error(
      `Empty response. Status: ${response.status} ${response.statusText}`.trim()
    )
    captureApiError(error, {
      endpoint,
      status: response.status,
      statusText: response.statusText,
      method,
      contentType
    })
    throw error
  }

  let data
  try {
    data = JSON.parse(text)
  } catch {
    const snippet = truncateBody(text)
    const error = new Error(
      `Invalid JSON response. Status: ${response.status} ${response.statusText}. Body: ${snippet}`.trim()
    )
    captureApiError(error, {
      endpoint,
      status: response.status,
      statusText: response.statusText,
      method,
      contentType,
      responseBody: snippet
    })
    throw error
  }

  if (!response.ok) {
    const responseMessage = data?.responseMessage ||
                        (typeof data === 'string' ? data : null) ||
                        undefined
    const errorMessage = responseMessage ||
                        `HTTP error! Status: ${response.status} ${response.statusText}`.trim()
    const error = new Error(errorMessage)
    captureApiError(error, {
      endpoint,
      status: response.status,
      statusText: response.statusText,
      method,
      contentType,
      responseMessage,
      responseBody: typeof data === 'string' ? truncateBody(data) : undefined
    })
    throw error
  }

  return data
}

async function apiGet(endpoint: string): Promise<any> {
  return apiRequest(endpoint)
}

async function apiPost(endpoint: string, body: any): Promise<any> {
  return apiRequest(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })
}

async function apiUpload(endpoint: string, formData: FormData): Promise<any> {
  return apiRequest(endpoint, {
    method: 'POST',
    body: formData
  })
}

export { apiGet, apiPost, apiUpload }
